import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { SettingsCard } from "./SettingsCard";
import { Copy, Check, Trash2, ExternalLink, KeyRound } from "lucide-react";

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  last_four: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

// O Swagger é servido por uma página estática do próprio app (public/api-docs.html),
// pois o Supabase força text/plain em HTML vindo das Edge Functions. A página lê a
// spec OpenAPI (application/json) exposta pela função public-api via ?spec=.
const OPENAPI_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-api/openapi.json`;
const DOCS_URL = `/api-docs.html?spec=${encodeURIComponent(OPENAPI_URL)}`;

const formatDate = (value: string | null) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return value;
  }
};

const ApiKeysTab: React.FC = () => {
  const { user } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [creating, setCreating] = useState(false);

  const [newKeyName, setNewKeyName] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadKeys = async () => {
    if (!user || !supabase) return;
    try {
      setLoadingData(true);
      const { data, error } = await supabase
        .from("account_api_keys")
        .select("id, name, key_prefix, last_four, last_used_at, revoked_at, created_at")
        .eq("account_id", user.account_id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setKeys((data as ApiKey[]) || []);
    } catch (error) {
      console.error("❌ [ApiKeysTab] Erro ao carregar chaves:", error);
      toast.error("Erro ao carregar as chaves de API");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    loadKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleCreate = async () => {
    if (!user || !supabase) return;
    if (!newKeyName.trim()) {
      toast.error("Informe um nome para a chave");
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-api-key", {
        body: { account_id: user.account_id, name: newKeyName.trim() },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Falha ao criar chave");

      setCreateDialogOpen(false);
      setNewKeyName("");
      setGeneratedKey(data.data.api_key);
      setCopied(false);
      await loadKeys();
      toast.success("Chave de API criada com sucesso");
    } catch (error) {
      console.error("❌ [ApiKeysTab] Erro ao criar chave:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao criar chave de API");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    if (!user || !supabase) return;
    if (!confirm(`Revogar a chave "${key.name}"? Essa ação não pode ser desfeita.`)) return;

    try {
      const { error } = await supabase
        .from("account_api_keys")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", key.id)
        .eq("account_id", user.account_id);

      if (error) throw error;
      await loadKeys();
      toast.success("Chave revogada");
    } catch (error) {
      console.error("❌ [ApiKeysTab] Erro ao revogar chave:", error);
      toast.error("Erro ao revogar a chave");
    }
  };

  const handleCopy = async () => {
    if (!generatedKey) return;
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      toast.success("Chave copiada");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar. Copie manualmente.");
    }
  };

  return (
    <div className="space-y-4 @container/apikeys">
      <SettingsCard className="flex flex-col @sm/apikeys:flex-row items-center gap-4 p-[17px]">
        <div className="flex items-center justify-center w-10 h-10 rounded-md bg-[#EFF6FF] dark:bg-[#1E3A8A]/40 shrink-0">
          <KeyRound className="w-5 h-5 text-[#2563EB] dark:text-[#60A5FA]" />
        </div>
        <div className="space-y-1 text-center @sm/apikeys:text-left flex-1">
          <h3 className="font-semibold text-sm text-[#0F172A] dark:text-[#FAFAFA]">Chaves de API</h3>
          <p className="text-sm text-[#64748B] dark:text-[#A1A1AA]">
            Gere chaves para consumir a API pública (cadastrar contato, enviar mensagem e ativar
            assistente). A chave é exibida apenas uma vez na criação.
          </p>
        </div>
        <div className="flex gap-2 mt-6 self-center @sm/apikeys:self-end">
          <Button
            variant="outline"
            className="min-w-[120px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]"
            onClick={() => window.open(DOCS_URL, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Documentação
          </Button>
          <Button className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8] min-w-[120px]" onClick={() => setCreateDialogOpen(true)}>
            Gerar nova chave
          </Button>
        </div>
      </SettingsCard>

      <SettingsCard className="p-[17px]">
        {loadingData ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]"></div>
          </div>
        ) : keys.length === 0 ? (
          <p className="text-sm text-[#64748B] dark:text-[#A1A1AA] text-center py-6">
            Nenhuma chave criada ainda.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-[#0F172A] dark:text-[#FAFAFA]">
              <thead>
                <tr className="text-left text-[#64748B] border-b border-[#E5E7EB] dark:text-[#A1A1AA] dark:border-[#3F3F46]">
                  <th className="py-2 pr-4 font-medium">Nome</th>
                  <th className="py-2 pr-4 font-medium">Chave</th>
                  <th className="py-2 pr-4 font-medium">Criada em</th>
                  <th className="py-2 pr-4 font-medium">Último uso</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id} className="border-b border-[#E5E7EB] dark:border-[#3F3F46]/50">
                    <td className="py-3 pr-4">{key.name}</td>
                    <td className="py-3 pr-4 font-mono text-[#475569] dark:text-[#D4D4D8]">
                      {key.key_prefix}…{key.last_four ?? ""}
                    </td>
                    <td className="py-3 pr-4 text-[#64748B] dark:text-[#A1A1AA]">{formatDate(key.created_at)}</td>
                    <td className="py-3 pr-4 text-[#64748B] dark:text-[#A1A1AA]">{formatDate(key.last_used_at)}</td>
                    <td className="py-3 pr-4">
                      {key.revoked_at ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 dark:bg-red-500/20 dark:text-red-300">
                          Revogada
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 dark:bg-green-500/20 dark:text-green-300">
                          Ativa
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-right">
                      {!key.revoked_at && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#DC2626] hover:text-[#B91C1C] hover:bg-red-50 dark:text-red-300 dark:hover:text-red-200 dark:hover:bg-red-500/10"
                          onClick={() => handleRevoke(key)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Revogar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>

      {/* Modal: criar nova chave */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">Gerar nova chave de API</DialogTitle>
            <DialogDescription className="text-[#64748B] dark:text-[#A1A1AA]">
              Dê um nome para identificar onde esta chave será usada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="api-key-name" className="text-[#0F172A] dark:text-[#FAFAFA]">Nome</Label>
            <Input
              id="api-key-name"
              placeholder="Ex.: Integração CRM"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
              }}
              className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creating} className="rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]">
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200">
              {creating ? "Gerando..." : "Gerar chave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: exibir chave gerada (uma única vez) */}
      <Dialog open={!!generatedKey} onOpenChange={(open) => !open && setGeneratedKey(null)}>
        <DialogContent className="rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">Sua nova chave de API</DialogTitle>
            <DialogDescription className="text-[#64748B] dark:text-[#A1A1AA]">
              Copie e guarde em local seguro. Por segurança, ela não será exibida novamente.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <Input readOnly value={generatedKey ?? ""} className="font-mono text-sm h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]" />
            <Button variant="outline" size="icon" onClick={handleCopy} className="rounded-xl border border-[#E5E7EB] dark:border-[#3F3F46]">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setGeneratedKey(null)} className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200">Concluí, guardei a chave</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiKeysTab;
